import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { ComparisonCard, Loader, Overline, Breadcrumbs } from "@/components/shared";
import { AdSlot } from "@/components/Ads";

const GROUPS = [
  ["All", "All"], ["🤖 AI Models", "AI Models"], ["🛠️ AI Tools", "AI Tools"], ["🎮 Gaming", "Gaming"],
  ["📱 Smartphones", "Smartphones"], ["🖥️ Hardware", "Hardware"], ["💻 Technology", "Tech"],
];

export default function Comparisons() {
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("All");
  const [filter, setFilter] = useState("Latest");
  useSEO({ title: "AI & Tech Comparisons (2026)", description: "The KLYROO comparison library — ChatGPT vs Claude vs Gemini, best AI for coding, PS5 vs Xbox, iPhone vs Samsung and more, with side-by-side tables and verdicts." });
  useEffect(() => {
    setItems(null);
    const f = filter === "Popular" ? "Popular" : filter === "Updated" ? "Updated" : null;
    api.comparisons({ category, filter: f }).then((d) => setItems(d.items));
  }, [category, filter]);

  return (
    <div className="klyroo-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Comparisons" }]} />
        <Overline>Head to head</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">AI Comparisons</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-lg">Detailed, up-to-date side-by-side comparisons across AI models, gaming, smartphones and hardware — each with a large comparison table and a practical verdict.</p>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-8 mb-6">
          <div className="flex flex-wrap gap-2">
            {GROUPS.map(([label, val]) => (
              <button key={val} onClick={() => setCategory(val)} data-testid={`comp-cat-${val.replace(/ /g, "-")}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === val ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>{label}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {["Latest", "Popular", "Updated"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`comp-filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${filter === f ? "border-klyroo-cyan/50 text-klyroo-cyan" : "border-white/10 text-slate-400 hover:border-white/30"}`}>
                {f === "Latest" ? "🔥 Latest" : f === "Popular" ? "🏆 Popular" : "🆕 Updated"}
              </button>
            ))}
          </div>
        </div>

        {!items ? <Loader /> : items.length === 0 ? (
          <p className="text-slate-500 py-20 text-center font-mono">No comparisons in this category yet.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="comparisons-grid">
            {items.flatMap((c, i) => {
              const cells = [<ComparisonCard key={c.slug} c={c} />];
              if ((i + 1) % 8 === 0 && i !== items.length - 1) {
                cells.push(<AdSlot key={`feed-ad-${i}`} placement="feed-square" className="h-full" />);
              }
              return cells;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
